import type { DagRowRead, ReorderLineItemRequest } from '@/types/dag'

export type RowRole = 'parent' | 'child' | 'standalone'

export function getRowRole(row: DagRowRead): RowRole {
  if (row.parent_line_item?.id) return 'child'
  if (row.cells['sku_level'] === 'Parent') return 'parent'
  return 'standalone'
}

// Rows sharing the same non-null cluster key move together as a block;
// standalone rows have no cluster key and form their own singleton block.
function clusterKey(row: DagRowRead): string | null {
  if (row.parent_line_item?.id) return row.parent_line_item.id
  if (row.cells['sku_level'] === 'Parent') return row.id
  return null
}

function blockKeyOf(row: DagRowRead): string {
  return clusterKey(row) ?? row.id
}

/**
 * Ordered ids of the full block (parent + all its children, or just itself
 * for a standalone row) containing `rowId`, in `rows`' original relative
 * order. Assumes clusters are contiguous in `rows` (true today — see
 * `_reposition_children_after_parent` on the backend).
 */
export function getBlockIds(rows: DagRowRead[], rowId: string): string[] {
  const target = rows.find(r => r.id === rowId)
  if (!target) return [rowId]
  const key = blockKeyOf(target)
  return rows.filter(r => blockKeyOf(r) === key).map(r => r.id)
}

export interface ReorderAnchor {
  rowId: string
  anchor: ReorderLineItemRequest
}

export interface ReorderPlan {
  anchors: ReorderAnchor[]
  newOrder: string[]
}

function groupIntoBlocks(
  ids: string[],
  byId: Map<string, DagRowRead>
): { key: string; ids: string[] }[] | null {
  const blocks: { key: string; ids: string[] }[] = []
  const seenKeys = new Set<string>()
  for (const id of ids) {
    const row = byId.get(id)
    if (!row) return null
    const key = blockKeyOf(row)
    const last = blocks[blocks.length - 1]
    if (last && last.key === key) {
      last.ids.push(id)
      continue
    }
    if (seenKeys.has(key)) return null // same block split by an interruption
    seenKeys.add(key)
    blocks.push({ key, ids: [id] })
  }
  return blocks
}

function replaceBlockInPlace(
  rows: DagRowRead[],
  blockIds: string[],
  newBlockOrder: string[]
): string[] {
  const ids = rows.map(r => r.id)
  const startIdx = ids.indexOf(blockIds[0])
  const result = [...ids]
  result.splice(startIdx, blockIds.length, ...newBlockOrder)
  return result
}

function computeChildReorder(
  rows: DagRowRead[],
  byId: Map<string, DagRowRead>,
  rawCandidateIds: string[],
  movedRow: DagRowRead
): ReorderPlan | null {
  const parentId = movedRow.parent_line_item!.id
  const originalBlockIds = getBlockIds(rows, movedRow.id)

  const newBlocks = groupIntoBlocks(rawCandidateIds, byId)
  if (!newBlocks) return null
  const newBlock = newBlocks.find(b => b.key === parentId)
  if (!newBlock) return null

  const sameMembers =
    newBlock.ids.length === originalBlockIds.length &&
    originalBlockIds.every(id => newBlock.ids.includes(id))
  if (!sameMembers) return null
  if (newBlock.ids[0] !== parentId) return null // parent must stay first
  if (newBlock.ids.join() === originalBlockIds.join()) return null // no-op

  const idx = newBlock.ids.indexOf(movedRow.id)
  const afterId = idx > 0 ? newBlock.ids[idx - 1] : undefined
  const beforeId = afterId ? undefined : newBlock.ids[idx + 1]
  if (!afterId && !beforeId) return null

  return {
    anchors: [
      {
        rowId: movedRow.id,
        anchor: afterId ? { after_id: afterId } : { before_id: beforeId },
      },
    ],
    newOrder: replaceBlockInPlace(rows, originalBlockIds, newBlock.ids),
  }
}

/**
 * Handles both the standalone (1-row block) and parent (whole-cluster) move
 * cases: locates the moved block's new neighbors in `rawCandidateIds`,
 * skipping over the block's own members (its former children may still sit
 * near the parent's new spot since AG Grid's managed drag only visually
 * relocates the single dragged node), and validates the block landed at a
 * cluster boundary rather than inside someone else's cluster.
 */
function computeBlockReorder(
  rows: DagRowRead[],
  byId: Map<string, DagRowRead>,
  rawCandidateIds: string[],
  movedRow: DagRowRead
): ReorderPlan | null {
  const blockIds = getBlockIds(rows, movedRow.id)
  const blockIdSet = new Set(blockIds)

  const dropIndex = rawCandidateIds.indexOf(movedRow.id)
  if (dropIndex === -1) return null

  let predecessorId: string | undefined
  for (let i = dropIndex - 1; i >= 0; i--) {
    const id = rawCandidateIds[i]
    if (!blockIdSet.has(id)) {
      predecessorId = id
      break
    }
  }
  let successorId: string | undefined
  for (let i = dropIndex + 1; i < rawCandidateIds.length; i++) {
    const id = rawCandidateIds[i]
    if (!blockIdSet.has(id)) {
      successorId = id
      break
    }
  }
  if (!predecessorId && !successorId) return null

  const predecessorKey = predecessorId
    ? blockKeyOf(byId.get(predecessorId)!)
    : null
  const successorKey = successorId ? blockKeyOf(byId.get(successorId)!) : null
  if (
    predecessorKey !== null &&
    successorKey !== null &&
    predecessorKey === successorKey
  ) {
    return null // landed inside someone else's cluster
  }

  const originalIdx = rows.findIndex(r => r.id === blockIds[0])
  const originalPrev = originalIdx > 0 ? rows[originalIdx - 1].id : undefined
  if (originalPrev === predecessorId) return null // no-op, unchanged position

  const otherIds = rawCandidateIds.filter(id => !blockIdSet.has(id))
  const insertAt = predecessorId ? otherIds.indexOf(predecessorId) + 1 : 0
  const newOrder = [...otherIds]
  newOrder.splice(insertAt, 0, ...blockIds)

  const anchors =
    computeMinimalAnchors(
      rows.map(r => r.id),
      newOrder
    ) ?? blockAnchors(blockIds, movedRow.id, predecessorId, successorId)

  return { anchors, newOrder }
}

function blockAnchors(
  blockIds: string[],
  movedRowId: string,
  predecessorId: string | undefined,
  successorId: string | undefined
): ReorderAnchor[] {
  const anchors: ReorderAnchor[] = [
    {
      rowId: movedRowId,
      anchor: predecessorId
        ? { after_id: predecessorId }
        : { before_id: successorId },
    },
  ]
  for (let i = 1; i < blockIds.length; i++) {
    anchors.push({ rowId: blockIds[i], anchor: { after_id: blockIds[i - 1] } })
  }
  return anchors
}

/**
 * Smallest set of single-row moves that turns `currentIds` into `newOrder`.
 *
 * Each backend reorder call moves one row, so moving a parent with N children
 * the naive way costs N+1 sequential round trips. Rows on a longest increasing
 * subsequence (by current position) can stay put; only the rest are moved —
 * e.g. dragging a 65-row PIAB below a standalone row moves just that one row
 * above the block. Anchors are ordered so every `after_id` / `before_id`
 * already sits in its final spot when its call runs.
 *
 * Returns null when the two lists don't hold the same ids.
 */
export function computeMinimalAnchors(
  currentIds: string[],
  newOrder: string[]
): ReorderAnchor[] | null {
  if (currentIds.length !== newOrder.length || newOrder.length < 2) return null
  const currentIndex = new Map(currentIds.map((id, i) => [id, i]))
  const positions: number[] = []
  for (const id of newOrder) {
    const idx = currentIndex.get(id)
    if (idx === undefined) return null
    positions.push(idx)
  }

  const keep = longestIncreasingSubsequence(positions)
  const firstKept = Math.min(...keep)
  const anchors: ReorderAnchor[] = []
  // Rows landing before the first unmoved row: walk right-to-left so each
  // before_id is already in place.
  for (let i = firstKept - 1; i >= 0; i--) {
    anchors.push({ rowId: newOrder[i], anchor: { before_id: newOrder[i + 1] } })
  }
  for (let i = firstKept + 1; i < newOrder.length; i++) {
    if (keep.has(i)) continue
    anchors.push({ rowId: newOrder[i], anchor: { after_id: newOrder[i - 1] } })
  }
  return anchors
}

/** Indexes (into `values`) of one longest strictly increasing subsequence. */
function longestIncreasingSubsequence(values: number[]): Set<number> {
  // Patience sorting: tails[k] = index of the smallest tail of an increasing
  // run of length k+1; prev links let us walk the winning run back.
  const tails: number[] = []
  const prev: number[] = new Array(values.length).fill(-1)
  for (let i = 0; i < values.length; i++) {
    let lo = 0
    let hi = tails.length
    while (lo < hi) {
      const mid = (lo + hi) >> 1
      if (values[tails[mid]] < values[i]) lo = mid + 1
      else hi = mid
    }
    if (lo > 0) prev[i] = tails[lo - 1]
    tails[lo] = i
  }
  const keep = new Set<number>()
  for (let i = tails[tails.length - 1] ?? -1; i !== -1; i = prev[i]) {
    keep.add(i)
  }
  return keep
}

/**
 * Validates a row drag-drop and, if valid, computes the anchor call(s) to
 * send to the backend plus the resulting full row-id order (for an
 * optimistic cache splice). Returns null when the drop is invalid or a
 * no-op — the caller should re-sync the grid to its unchanged row order.
 *
 * `rawCandidateIds` is the grid's post-drop real-row node order (option rows
 * already filtered out).
 */
export function computeReorderPlan(
  rows: DagRowRead[],
  rawCandidateIds: string[],
  movedRowId: string
): ReorderPlan | null {
  const byId = new Map(rows.map(r => [r.id, r]))
  const movedRow = byId.get(movedRowId)
  if (!movedRow) return null

  return getRowRole(movedRow) === 'child'
    ? computeChildReorder(rows, byId, rawCandidateIds, movedRow)
    : computeBlockReorder(rows, byId, rawCandidateIds, movedRow)
}
